import React, { useState } from "react";
import Navbar from "../common/navbar";
import { Container, Button, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import authService from "../../services/authService";

const ForgetPassword = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const validatePassword = (password) => {
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasMinLength = password.length >= 8;
    return hasLowercase && hasUppercase && hasNumber && hasMinLength;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setStep(2);
    } catch (error) {
      console.error(
        "Forgot Password error:",
        error.response?.data || error.message
      );
      setError(
        error.response?.data?.message ||
          "This email address is not registered. Enter correct email."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Confirm password does not match. Please try again.");
      return;
    }
    if (!validatePassword(password)) {
      setError(
        "Password must be 8 characters, including 1 uppercase, 1 lowercase & 1 number"
      );
      return;
    }
    setLoading(true);
    try {
      const response = await authService.resetPassword(
        code,
        password,
        confirmPassword
      );
      if (response.message === "Password reset successfully") {
        navigate("/login", {
          state: {
            successMessage:
              "Password has been reset successfully. You can now log in",
          },
        });
      } else {
        setError("Invalid code. Please try again.");
      }
    } catch (error) {
      console.error(
        "Verify Code error:",
        error.response?.data || error.message
      );
      setError(
        error.response?.data?.message || "Invalid code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await authService.forgotPassword(email);
      setError(""); // Clear any previous error
      alert("A new confirmation code has been sent to your email.");
    } catch (error) {
      setError("Failed to resend code. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="body-font">
      <Navbar hideNavLinks={true} />
      <Container className="text-center empty-space-mt w-lg-25">
        <h1 className="text-nowrap fs-five-title my-4 d-flex justify-content-center align-items-center">
          {step === 1 ? "Forgot Password" : "Enter Code and Reset Password"}
          <div className="mx-1 purple-color mb-0 fs-5 righteousy-font">
            <span className="fs-3">Nouraa</span>
            <span className="fs-two">.ai</span>
          </div>{" "}
        </h1>
        <p className="gray-color">
          Remember your password?{" "}
          <Link
            className="purple-color text-decoration-none fw-bold"
            to="/login"
          >
            Log in
          </Link>
        </p>
        {step === 1 && (
          <form onSubmit={handleEmailSubmit} className="login-form my-4">
            <div className="form-group mb-4">
              <label className="text-start w-100 w-md-50 w-lg-25 mb-1 fw-bold gray-color">
                Email
              </label>
              <input
                type="email"
                value={email}
                placeholder="Enter your email"
                onChange={handleEmailChange}
                className="form-control w-100 w-md-50 w-lg-25 mx-auto"
                required
              />
            </div>
            {error && (
              <p className="text-danger text-start w-100 w-md-50 w-lg-25 mx-auto">
                {error}
              </p>
            )}
            <Button
              className="btn-primary-style fs-one fw-bold w-100 w-md-50 w-lg-25"
              type="submit"
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Send Code"}
            </Button>
          </form>
        )}
        {step === 2 && (
          <>
            <p className="text-success">
              A confirmation code has been sent to your inbox. Please follow the
              instructions provided.
            </p>
            <p className="gray-color">
              Note: Please check your Spam folder as well
            </p>
            <form onSubmit={handleCodeSubmit} className="login-form my-4">
              <div className="form-group mb-4">
                <label className="text-start w-100 w-md-50 w-lg-25 mb-1 fw-bold gray-color">
                  Confirmation Code
                </label>
                <input
                  type="text"
                  value={code}
                  placeholder="Enter Confirmation Code"
                  onChange={handleCodeChange}
                  className="form-control w-100 w-md-50 w-lg-25 mx-auto"
                  required
                />
              </div>
              <div className="form-group mb-4 position-relative">
                <label className="text-start w-100 w-md-50 w-lg-25 mb-1 fw-bold gray-color">
                  New Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  placeholder="Enter your new password"
                  onChange={handlePasswordChange}
                  className="form-control w-100 w-md-50 w-lg-25 mx-auto"
                  required
                />
                <span
                  onClick={togglePasswordVisibility}
                  className="position-absolute top-50 end-0 me-3"
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={
                      showPassword ? "/assets/eye-slash.svg" : "/assets/eye.svg"
                    }
                    alt="Toggle visibility"
                  />
                </span>
              </div>
              <div className="form-group position-relative">
                <label className="text-start w-100 w-md-50 w-lg-25 mb-1 fw-bold gray-color">
                  Confirm Password
                </label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  placeholder="Confirm your new password"
                  onChange={handleConfirmPasswordChange}
                  className="form-control w-100 w-md-50 w-lg-25 mx-auto"
                  required
                />
                <span
                  onClick={toggleConfirmPasswordVisibility}
                  className="position-absolute top-50 end-0 me-3"
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={
                      showConfirmPassword
                        ? "/assets/eye-slash.svg"
                        : "/assets/eye.svg"
                    }
                    alt="Toggle visibility"
                  />
                </span>
              </div>
              <small className="gray-color d-block text-start mt-1">
                Make sure both the password match
              </small>
              <div>
                <p className="gray-color fw-bold text-start mt-2">
                  Your password must contain:
                </p>
                <ul className="list-unstyled gray-color text-start my-3">
                  <li>At least one lowercase letter</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                  <li>At least 8 characters long</li>
                </ul>
              </div>
              {error && (
                <p className="text-danger text-start w-100 w-md-50 w-lg-25 mx-auto">
                  {error}
                </p>
              )}
              <Button
                className="btn-primary-style fs-one fw-bold w-100 w-md-50 w-lg-25"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Verify Code and Reset Password"
                )}
              </Button>
            </form>
            <p className="gray-color">
              Didn’t receive the confirmation code?{" "}
              <span
                className="purple-color text-decoration-none fw-bold"
                style={{ cursor: "pointer" }}
                onClick={handleResendCode}
              >
                Resend
              </span>
            </p>
          </>
        )}
      </Container>
    </div>
  );
};

export default ForgetPassword;
